<template>
  <section class="feature-tutorial" aria-labelledby="feature-tutorial-heading">
    <div class="feature-tutorial-shell">
      <div class="feature-tutorial-copy">
        <p class="feature-tutorial-eyebrow">Watch · {{ tutorial.length }}</p>
        <h2 id="feature-tutorial-heading">{{ tutorial.title }}</h2>
        <p>{{ tutorial.description }}</p>
        <NuxtLink :to="localePath(`/tutorials/${tutorial.slug}`)">Read the transcript and chapter guide →</NuxtLink>
      </div>
      <div class="feature-tutorial-player">
        <iframe
          v-if="playing"
          ref="playerFrame"
          :src="`https://www.youtube-nocookie.com/embed/${tutorial.youtubeId}?autoplay=1&rel=0${tutorial.start ? `&start=${tutorial.start}` : ''}`"
          :title="tutorial.title"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowfullscreen
        ></iframe>
        <button v-else type="button" :aria-label="`Play video: ${tutorial.title}`" @click="play">
          <img
            :src="`https://i.ytimg.com/vi/${tutorial.youtubeId}/maxresdefault.jpg`"
            :alt="`Video thumbnail: ${tutorial.title}`"
            width="1280"
            height="720"
            loading="lazy"
            decoding="async"
          />
          <span class="feature-tutorial-play" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z" /></svg></span>
        </button>
      </div>
      <p class="feature-tutorial-note">Recorded in a demonstration workspace. Check current plans and settings for availability.</p>
    </div>
  </section>
</template>
<script setup>
// Poster first; the YouTube player (youtube-nocookie) loads only after a click. Which video a page
// shows comes from data/feature-videos.ts; pages without one don't render this section.
const props = defineProps({ tutorial: { type: Object, required: true } });
const { localePath } = useWorkspaceLocale();
const playing = ref(false);
const playerFrame = ref(null);

async function play() {
  playing.value = true;
  await nextTick();
  playerFrame.value?.focus();
}

useHead(() => ({
  script: [
    {
      key: "feature-tutorial-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: props.tutorial.title,
        description: props.tutorial.videoDescription,
        thumbnailUrl: [
          `https://i.ytimg.com/vi/${props.tutorial.youtubeId}/maxresdefault.jpg`,
          `https://i.ytimg.com/vi/${props.tutorial.youtubeId}/hqdefault.jpg`,
        ],
        uploadDate: props.tutorial.uploadDate,
        duration: props.tutorial.duration,
        embedUrl: `https://www.youtube-nocookie.com/embed/${props.tutorial.youtubeId}`,
        url: `https://www.youtube.com/watch?v=${props.tutorial.youtubeId}`,
        publisher: { "@type": "Organization", name: "Workspace369" },
      }),
    },
  ],
}));
</script>
<style scoped>
.feature-tutorial { padding: clamp(3rem, 6vw, 5rem) 1.25rem; background: #f8fafc; color: #0f172a; border-block: 1px solid #e2e8f0; }
.feature-tutorial-shell { display: grid; gap: 1.5rem 3rem; max-width: 84rem; margin: auto; align-items: center; }
.feature-tutorial-eyebrow { color: #0e7490; font-size: 0.78rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.feature-tutorial h2 { margin-top: 0.5rem; font-size: clamp(1.6rem, 2.6vw, 2.2rem); font-weight: 740; letter-spacing: -0.022em; line-height: 1.15; text-wrap: balance; }
.feature-tutorial-copy p:not(.feature-tutorial-eyebrow) { margin: 1rem 0 1.25rem; color: #475569; line-height: 1.7; }
.feature-tutorial a { color: #0e7490; font-weight: 600; text-decoration: underline; text-underline-offset: 4px; }
.feature-tutorial-player { position: relative; aspect-ratio: 16 / 9; overflow: hidden; border: 1px solid #e2e8f0; border-radius: 10px; background: #0f172a; box-shadow: 0 24px 48px -28px rgb(15 23 42 / 0.45); }
.feature-tutorial-player iframe { width: 100%; height: 100%; border: 0; }
.feature-tutorial-player button { position: absolute; inset: 0; display: block; width: 100%; padding: 0; border: 0; background: none; cursor: pointer; }
.feature-tutorial-player img { width: 100%; height: 100%; object-fit: cover; }
.feature-tutorial-play { position: absolute; left: 50%; top: 50%; display: grid; place-items: center; width: 4.25rem; height: 3rem; border-radius: 10px; background: rgb(15 23 42 / 0.82); color: #fff; transform: translate(-50%, -50%); transition: background 0.15s; }
.feature-tutorial-play svg { width: 1.6rem; height: 1.6rem; fill: currentColor; }
.feature-tutorial-player button:hover .feature-tutorial-play { background: #0e7490; }
.feature-tutorial-note { grid-column: 1 / -1; color: #64748b; font-size: 0.875rem; }
.feature-tutorial button:focus-visible, .feature-tutorial a:focus-visible { outline: 3px solid #06b6d4; outline-offset: 4px; }
.dark .feature-tutorial { background: #07101f; color: #f1f5f9; border-color: #1e293b; }
.dark .feature-tutorial-eyebrow, .dark .feature-tutorial a { color: #67e8f9; }
.dark .feature-tutorial-copy p:not(.feature-tutorial-eyebrow) { color: #cbd5e1; }
.dark .feature-tutorial-player { border-color: #1e293b; }
.dark .feature-tutorial-note { color: #94a3b8; }
@media (min-width: 900px) {
  .feature-tutorial-shell { grid-template-columns: minmax(0, 5fr) minmax(0, 7fr); }
}
@media (prefers-reduced-motion: reduce) { .feature-tutorial-play { transition: none; } }
</style>
